import { useState, useCallback, useEffect, useMemo } from 'react';
import { useFlowBuilderStore } from '@/stores/flow-builder-store';
import type { RecentEvent } from '@/types/field-mapping-flow';

/**
 * Hook for managing recent event selection and providing
 * the selected event's data for field path extraction.
 *
 * - Filters events by the selected branch's event type
 *   ("---default" shows all, "eventtype=XYZ" shows only that type).
 * - Auto-selects the latest (top) event when the filtered list changes.
 */
export function useRecentEvents() {
  const allRecentEvents = useFlowBuilderStore((s) => s.recentEvents);
  const selectedBranchId = useFlowBuilderStore((s) => s.selectedBranchId);
  const fieldMappings = useFlowBuilderStore((s) => s.fieldMappings);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  // Get the selected branch's filterValue to determine event type filtering
  const branchFilterValue = selectedBranchId
    ? fieldMappings[selectedBranchId]?.filterValue ?? ''
    : '';

  // Extract the event type ID from the branch filter (e.g. "eventtype=abc123" → "abc123")
  const branchEventTypeId = branchFilterValue.startsWith('eventtype=')
    ? branchFilterValue.replace('eventtype=', '')
    : '';

  // Filter recent events by the selected branch's event type
  const filteredEvents = useMemo(() => {
    // Default branch or non-event branch → show all events
    if (!branchEventTypeId || branchFilterValue === '---default') {
      return allRecentEvents;
    }
    // Filter to only events matching this branch's event type
    return allRecentEvents.filter((e) => e.eventTypeValue === branchEventTypeId);
  }, [allRecentEvents, branchEventTypeId, branchFilterValue]);

  // Auto-select the latest (first) event whenever the filtered list changes
  useEffect(() => {
    if (filteredEvents.length > 0) {
      // Always select the latest event (already sorted desc by ct)
      const latestId = filteredEvents[0].id;
      if (selectedEventId !== latestId) {
        setSelectedEventId(latestId);
      }
    } else {
      // No events — clear selection
      if (selectedEventId !== null) {
        setSelectedEventId(null);
      }
    }
  }, [filteredEvents]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedEvent: RecentEvent | null =
    filteredEvents.find((e) => e.id === selectedEventId) ?? null;

  const selectedEventData: Record<string, unknown> | null =
    selectedEvent?.actualSourceObj ?? null;

  const selectEvent = useCallback((eventId: string | null) => {
    setSelectedEventId(eventId);
  }, []);

  return {
    recentEvents: filteredEvents,
    selectedEventId,
    selectedEvent,
    selectedEventData,
    selectEvent,
    branchEventTypeId,
  };
}
